import type { CommonMetricContext } from "../../entities/forecast_cell_context";
import type { CommonViewContextV1 } from "../../entities/common_view_context";

export type CommonScopeIntent = {
  version: "CommonScopeIntent.v1";
  surfacePath: string;
  run: {
    inputId: string;
    workflowId: string;
    settingsNameAndVersion: string;
    runAttemptId: string;
  };
};

export type CommonCurrentViewDataV1 = {
  version: "CommonCurrentViewData.v1";
  surfaceId:
    | "demand"
    | "supply"
    | "forecast-tracking"
    | "po-inbox"
    | "inventory-health";
  receiptId: string;
};

const CURRENT_VIEW_RECEIPT_ID = /^rvr2_[a-f0-9]{64}$/;
const PO_INBOX_CURRENT_VIEW_RECEIPT_ID = /^rvr3_[a-f0-9]{64}$/;
const INVENTORY_HEALTH_CURRENT_VIEW_RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;

export const isCommonCurrentViewReceiptId = (value: unknown): value is string =>
  typeof value === "string" && CURRENT_VIEW_RECEIPT_ID.test(value);

export const isCommonPoInboxCurrentViewReceiptId = (
  value: unknown,
): value is string =>
  typeof value === "string" && PO_INBOX_CURRENT_VIEW_RECEIPT_ID.test(value);

export const isCommonInventoryHealthCurrentViewReceiptId = (
  value: unknown,
): value is string =>
  typeof value === "string" &&
  INVENTORY_HEALTH_CURRENT_VIEW_RECEIPT_ID.test(value);

export const isCommonCurrentViewDataReceipt = (
  data: CommonCurrentViewDataV1,
): boolean => {
  if (data.surfaceId === "po-inbox") {
    return isCommonPoInboxCurrentViewReceiptId(data.receiptId);
  }
  if (data.surfaceId === "inventory-health") {
    return isCommonInventoryHealthCurrentViewReceiptId(data.receiptId);
  }
  return isCommonCurrentViewReceiptId(data.receiptId);
};

export type EmbeddedTurnRequest = {
  version: "EmbeddedTurnRequest.v1";
  requestId: string;
  conversationId?: string;
  /** Untrusted customer-selection intent. The server remains authoritative. */
  organizationId: string;
  message: {
    id: string;
    text: string;
  };
  metricContext?: CommonMetricContext;
  /** Descriptive UI state only. Never authoritative for authorization or data access. */
  viewContext?: CommonViewContextV1;
  /** Opaque accepted-view binding. The server and Adapter remain authoritative. */
  currentViewData?: CommonCurrentViewDataV1;
  /** Untrusted scope intent. The server remains authoritative for authorization. */
  scopeIntent?: CommonScopeIntent;
};

type TurnEventBase = {
  version: "nucleus.turn.v1";
  eventId: string;
  sequence: number;
  turnId: string;
  conversationId: string;
  timestamp: string;
};

export type NucleusTraceStatus =
  | "full_trace"
  | "partial"
  | "unsupported"
  | "not_applicable";

export type NucleusReconciliationStatus =
  | "matched"
  | "partial"
  | "unsupported"
  | "not_applicable";

export type NucleusTurnOutcome =
  | "answered_grounded"
  | "partial_grounded"
  | "answered_conversational"
  | "answered_partial"
  | "blocked_missing_capability"
  | "blocked_missing_data"
  | "unsupported"
  | "infra_error";

const COMPLETED_NUCLEUS_TURN_OUTCOMES = new Set<NucleusTurnOutcome>([
  "answered_grounded",
  "partial_grounded",
  "answered_conversational",
  "answered_partial",
]);

export type NucleusTurnEvent =
  | (TurnEventBase & {
      type: "turn.started";
      messageId: string;
    })
  | (TurnEventBase & {
      type: "work.updated";
      workId: string;
      status: "started" | "completed" | "failed";
      label: string;
      evidenceIds?: string[];
    })
  | (TurnEventBase & {
      type: "text.delta";
      messageId: string;
      delta: string;
    })
  | (TurnEventBase & {
      type: "evidence.available";
      evidence: {
        id: string;
        kind?: "documentation" | "run_data" | "lineage";
        title: string;
        summary?: string;
        document?: {
          title: string;
          section?: string;
          excerpt?: string;
          freshness?: string;
        };
      };
      label: string;
    })
  | (TurnEventBase & {
      type: "result.available";
      status: NucleusTraceStatus;
      reconciliationStatus: NucleusReconciliationStatus;
      evidenceIds: string[];
      outcome?: NucleusTurnOutcome;
    })
  | (TurnEventBase & {
      type: "turn.completed";
      messageId: string;
      finishReason: "completed" | "unsupported";
    })
  | (TurnEventBase & {
      type: "turn.failed";
      code: string;
      message: string;
      retryable: boolean;
    })
  | (TurnEventBase & {
      type: "turn.cancelled";
      messageId: string;
    })
  | (TurnEventBase & { type: "heartbeat" });

const TURN_EVENT_TYPES = new Set<NucleusTurnEvent["type"]>([
  "turn.started",
  "work.updated",
  "text.delta",
  "evidence.available",
  "result.available",
  "turn.completed",
  "turn.failed",
  "turn.cancelled",
  "heartbeat",
]);
const TERMINAL_EVENT_TYPES = new Set<NucleusTurnEvent["type"]>([
  "turn.completed",
  "turn.failed",
  "turn.cancelled",
]);
const MAX_SSE_EVENT_CHARS = 128 * 1024;
const MAX_SSE_BUFFER_CHARS = 256 * 1024;
const MAX_HELD_TEXT_CHARS = 4 * 1024;
const HELD_TEXT_TOKENS = 3;
const TRUSTED_CHAT_GATEWAY_HOST =
  /^chatgateway\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.atomic\.supply$/i;
const LOCAL_NUCLEUS_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

export class NucleusSseError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "NucleusSseError";
  }
}

export function isAllowedNucleusStreamUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }

  if (url.username || url.password) return false;
  if (LOCAL_NUCLEUS_HOSTS.has(url.hostname)) {
    return url.protocol === "http:" || url.protocol === "https:";
  }
  return (
    url.protocol === "https:" &&
    url.port === "" &&
    TRUSTED_CHAT_GATEWAY_HOST.test(url.hostname)
  );
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const isBoundedString = (value: unknown, maxLength: number): value is string =>
  isNonEmptyString(value) && value.length <= maxLength;

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length <= 100 && value.every(isNonEmptyString);

const isEvidenceIdArray = (value: unknown): value is string[] =>
  isStringArray(value) &&
  value.every((item) => /^source-[1-9][0-9]*$/.test(item));

const BASE_EVENT_KEYS = [
  "version",
  "type",
  "eventId",
  "sequence",
  "turnId",
  "conversationId",
  "timestamp",
] as const;

const hasExactKeys = (
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean => {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
};

const exactEventKeys = (
  candidate: Record<string, unknown>,
  fields: readonly string[],
): boolean => hasExactKeys(candidate, [...BASE_EVENT_KEYS, ...fields]);

const isEvidenceDocument = (
  value: unknown,
): value is NonNullable<
  Extract<
    NucleusTurnEvent,
    { type: "evidence.available" }
  >["evidence"]["document"]
> => {
  if (!isRecord(value)) return false;
  const keys = [
    "title",
    ...(value.section === undefined ? [] : ["section"]),
    ...(value.excerpt === undefined ? [] : ["excerpt"]),
    ...(value.freshness === undefined ? [] : ["freshness"]),
  ];
  return (
    hasExactKeys(value, keys) &&
    isBoundedString(value.title, 256) &&
    (value.section === undefined || isBoundedString(value.section, 200)) &&
    (value.excerpt === undefined || isBoundedString(value.excerpt, 2_000)) &&
    (value.freshness === undefined || isBoundedString(value.freshness, 120))
  );
};

const isEvidence = (
  value: unknown,
): value is Extract<
  NucleusTurnEvent,
  { type: "evidence.available" }
>["evidence"] => {
  if (!isRecord(value)) return false;
  const keys = [
    "id",
    "title",
    ...(value.kind === undefined ? [] : ["kind"]),
    ...(value.summary === undefined ? [] : ["summary"]),
    ...(value.document === undefined ? [] : ["document"]),
  ];
  return (
    hasExactKeys(value, keys) &&
    isNonEmptyString(value.id) &&
    /^source-[1-9][0-9]*$/.test(value.id) &&
    isBoundedString(value.title, 256) &&
    (value.kind === undefined ||
      value.kind === "documentation" ||
      value.kind === "run_data" ||
      value.kind === "lineage") &&
    (value.summary === undefined || isBoundedString(value.summary, 1_000)) &&
    (value.document === undefined || isEvidenceDocument(value.document))
  );
};

export const isNucleusTurnOutcome = (
  value: unknown,
): value is NucleusTurnOutcome =>
  value === "answered_grounded" ||
  value === "partial_grounded" ||
  value === "answered_conversational" ||
  value === "answered_partial" ||
  value === "blocked_missing_capability" ||
  value === "blocked_missing_data" ||
  value === "unsupported" ||
  value === "infra_error";

export const nucleusTurnOutcomeFinishReason = (
  outcome: NucleusTurnOutcome,
): "completed" | "unsupported" =>
  COMPLETED_NUCLEUS_TURN_OUTCOMES.has(outcome) ? "completed" : "unsupported";

export const isValidNucleusTurnOutcomeStatus = ({
  outcome,
  status,
  reconciliationStatus,
}: {
  outcome: NucleusTurnOutcome;
  status: NucleusTraceStatus;
  reconciliationStatus: NucleusReconciliationStatus;
}): boolean => {
  if (outcome === "answered_grounded") {
    return status === "full_trace" && reconciliationStatus === "matched";
  }
  if (outcome === "partial_grounded" || outcome === "answered_partial") {
    return status === "partial" && reconciliationStatus === "partial";
  }
  if (outcome === "answered_conversational") {
    return (
      status === "not_applicable" && reconciliationStatus === "not_applicable"
    );
  }
  return status === "unsupported" && reconciliationStatus === "unsupported";
};

const validResultAvailable = (candidate: Record<string, unknown>): boolean => {
  if (!isEvidenceIdArray(candidate.evidenceIds)) return false;
  const evidenceIds = candidate.evidenceIds;
  const outcome = candidate.outcome;

  if (outcome === undefined) {
    if (
      candidate.status === "not_applicable" ||
      candidate.reconciliationStatus === "not_applicable"
    ) {
      return false;
    }
    return (
      (candidate.status === "full_trace" ||
        candidate.status === "partial" ||
        candidate.status === "unsupported") &&
      (candidate.reconciliationStatus === "matched" ||
        candidate.reconciliationStatus === "partial" ||
        candidate.reconciliationStatus === "unsupported")
    );
  }

  if (
    !isNucleusTurnOutcome(outcome) ||
    !isValidNucleusTurnOutcomeStatus({
      outcome,
      status: candidate.status as NucleusTraceStatus,
      reconciliationStatus:
        candidate.reconciliationStatus as NucleusReconciliationStatus,
    })
  ) {
    return false;
  }
  return outcome === "answered_grounded" || outcome === "partial_grounded"
    ? evidenceIds.length > 0
    : evidenceIds.length === 0;
};

function isStructurallyValidEvent(
  candidate: Record<string, unknown>,
): candidate is NucleusTurnEvent {
  if (
    candidate.version !== "nucleus.turn.v1" ||
    typeof candidate.type !== "string" ||
    !TURN_EVENT_TYPES.has(candidate.type as NucleusTurnEvent["type"]) ||
    !isNonEmptyString(candidate.eventId) ||
    !Number.isInteger(candidate.sequence) ||
    Number(candidate.sequence) < 0 ||
    !isNonEmptyString(candidate.turnId) ||
    !isNonEmptyString(candidate.conversationId) ||
    !isNonEmptyString(candidate.timestamp) ||
    !Number.isFinite(Date.parse(candidate.timestamp))
  ) {
    return false;
  }

  switch (candidate.type) {
    case "turn.started":
      return (
        exactEventKeys(candidate, ["messageId"]) &&
        isNonEmptyString(candidate.messageId)
      );
    case "work.updated":
      return (
        exactEventKeys(candidate, [
          "workId",
          "status",
          "label",
          ...(candidate.evidenceIds === undefined ? [] : ["evidenceIds"]),
        ]) &&
        isNonEmptyString(candidate.workId) &&
        (candidate.status === "started" ||
          candidate.status === "completed" ||
          candidate.status === "failed") &&
        isNonEmptyString(candidate.label) &&
        (candidate.evidenceIds === undefined ||
          isEvidenceIdArray(candidate.evidenceIds))
      );
    case "text.delta":
      return (
        exactEventKeys(candidate, ["messageId", "delta"]) &&
        isNonEmptyString(candidate.messageId) &&
        isNonEmptyString(candidate.delta)
      );
    case "evidence.available":
      return (
        exactEventKeys(candidate, ["evidence", "label"]) &&
        isEvidence(candidate.evidence) &&
        isNonEmptyString(candidate.label)
      );
    case "result.available":
      return (
        exactEventKeys(candidate, [
          "status",
          "reconciliationStatus",
          "evidenceIds",
          ...(candidate.outcome === undefined ? [] : ["outcome"]),
        ]) && validResultAvailable(candidate)
      );
    case "turn.completed":
      return (
        exactEventKeys(candidate, ["messageId", "finishReason"]) &&
        isNonEmptyString(candidate.messageId) &&
        (candidate.finishReason === "completed" ||
          candidate.finishReason === "unsupported")
      );
    case "turn.failed":
      return (
        exactEventKeys(candidate, ["code", "message", "retryable"]) &&
        candidate.code === "TURN_FAILED" &&
        candidate.message === "We couldn't complete that request." &&
        candidate.retryable === true
      );
    case "turn.cancelled":
      return (
        exactEventKeys(candidate, ["messageId"]) &&
        isNonEmptyString(candidate.messageId)
      );
    case "heartbeat":
      return exactEventKeys(candidate, []);
    default:
      return false;
  }
}

const UNSAFE_CUSTOMER_TEXT = new RegExp(
  [
    "\\barn:(?:aws|aws-cn|aws-us-gov):",
    "\\bs3://",
    "\\bhttps?://[^\\s]*(?:amazonaws\\.com|awsstatic\\.com)",
    "\\b[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\\.[a-z0-9-]+)+\\b",
    "\\bx-amz-(?:algorithm|credential|date|expires|security-token|signature|signedheaders)\\b",
    "\\b(?:aws\\s+account(?:\\s+id)?|aws_account_id)\\s*(?::|=|is)?\\s*\\d{12}\\b",
    "\\b(?:aws|lambda|dynamodb|cloudwatch|cloudformation|codebuild|codepipeline|agentcore|bedrock|step\\s+functions?|ecs(?:\\s+(?:service|cluster|task))?)\\b",
    "\\b(?:object\\s+key|run\\s+(?:attempt\\s+)?id|etag)\\b",
    "\\bbucket\\s+(?:named\\s+)?(?=[a-z0-9.-]*[-.])[a-z0-9](?:[a-z0-9.-]{1,61}[a-z0-9])\\b",
    "\\b[a-z0-9][a-z0-9._-]{1,127}-(?:development|staging|production)\\b",
    "\\b(?:accessdenied|validation|resource(?:notfound|inuse)|throttling|internalserver|serviceunavailable|unrecognizedclient|expiredtoken)exception\\b",
    "\\b(?:atomic_(?:master|pivots)|(?:sales_order|purchase_order|inventory|forecast)_master|main_(?:po_orders|sales_orders|inventory|forecast)|(?:organization|tenant|request|turn|conversation|receipt|run|input|workflow|object|source_table)_id|run_attempt_id|settings_name_and_version|(?:order_placed|order_delivery|purchase_order)_(?:date|week|month))\\b",
    "\\b(?:common\\.documentation\\.search|nucleusadapter|chatgateway|atomic-(?:chat-gateway|steps|orchestrator|view-transforms|ai-sdk)|perspective-server)\\b",
    "\\bcommon\\.[a-z0-9_.-]+\\b",
    "\\b(?:primitive|tool(?: call)?)\\s+[a-z][a-z0-9.-]*(?:_[a-z0-9.-]+)+\\b",
    "\\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\b",
    "\\b(?:sha256:)?[0-9a-f]{64}\\b",
    "\\b(?:insert\\s+into|delete\\s+from|drop\\s+table|alter\\s+table|create\\s+table)\\b",
  ].join("|"),
  "i",
);

// An uppercase SQL command is itself an unsafe cue. Rejecting it immediately
// keeps a tokenized query from aging out of the rolling stream holdback before
// a later FROM/table token arrives. Lowercase customer prose such as “select a
// week” remains valid; complete lowercase SQL is still rejected above.
const UNSAFE_UPPERCASE_SQL_TEXT =
  /\b(?:SELECT|INSERT\s+INTO|DELETE\s+FROM|DROP\s+TABLE|ALTER\s+TABLE|CREATE\s+TABLE)\b/;
const SQL_SELECT_START = /\bselect\b/giu;
const SELECT_PROSE_DETERMINERS = new Set([
  "a",
  "an",
  "the",
  "your",
  "this",
  "that",
  "one",
  "another",
  "any",
  "which",
  "each",
]);
const SELECT_PROSE_SQL_WORDS = new Set([
  "as",
  "case",
  "distinct",
  "group",
  "having",
  "into",
  "join",
  "limit",
  "union",
  "when",
  "where",
]);

const KNOWN_CUSTOMERS = [
  "starface_world",
  "cora",
  "brooklinen",
  "goodr",
  "vincero",
  "oofos",
  "glowco",
] as const;

function sentenceBoundaryAfter(
  value: string,
  start: number,
): number | undefined {
  let quote: "'" | '"' | "`" | undefined;
  let squareDepth = 0;
  let parenthesisDepth = 0;
  for (let index = start; index < value.length; index += 1) {
    const character = value[index];
    if (quote) {
      if (character === "\\") {
        index += 1;
      } else if (character === quote) {
        if (quote === "'" && value[index + 1] === "'") index += 1;
        else quote = undefined;
      }
      continue;
    }
    if (character === "'" || character === '"' || character === "`") {
      quote = character;
      continue;
    }
    if (character === "[") squareDepth += 1;
    else if (character === "]" && squareDepth > 0) squareDepth -= 1;
    else if (character === "(") parenthesisDepth += 1;
    else if (character === ")" && parenthesisDepth > 0) parenthesisDepth -= 1;
    else if (
      squareDepth === 0 &&
      parenthesisDepth === 0 &&
      (character === "." || character === "?" || character === "!") &&
      (index + 1 === value.length || /\s/u.test(value[index + 1])) &&
      !(character === "." && /\d/u.test(value[index - 1] ?? ""))
    ) {
      return index + 1;
    }
  }
  return undefined;
}

function isRecognizedSelectProse(
  value: string,
  completeSentence: boolean,
): boolean {
  let prose = value.trim();
  if (completeSentence) prose = prose.replace(/[.!?]\s*$/u, "").trim();
  if (
    prose.length === 0 ||
    prose.length > 512 ||
    !/^[a-z0-9 ,-]+$/iu.test(prose)
  ) {
    return false;
  }
  const clauses = prose.split(",");
  if (
    clauses.length > 2 ||
    (clauses.length === 2 &&
      clauses.some((clause) => clause.trim().split(/\s+/u).length < 3))
  ) {
    return false;
  }
  const words = prose.toLocaleLowerCase("en-US").split(/\s+/u);
  if (
    words.length < 2 ||
    !SELECT_PROSE_DETERMINERS.has(words[0]) ||
    words.some((word) => SELECT_PROSE_SQL_WORDS.has(word))
  ) {
    return false;
  }
  const fromIndexes = words.flatMap((word, index) =>
    word === "from" ? [index] : [],
  );
  if (fromIndexes.length === 0) return true;
  if (fromIndexes.length !== 1 || !completeSentence) return false;
  const fromIndex = fromIndexes[0];
  const afterFrom = words.slice(fromIndex + 1);
  return (
    fromIndex >= 2 &&
    afterFrom.length >= 2 &&
    SELECT_PROSE_DETERMINERS.has(afterFrom[0])
  );
}

function rawContainsUnsafeCompletedSelect(value: string): boolean {
  SQL_SELECT_START.lastIndex = 0;
  for (const match of value.matchAll(SQL_SELECT_START)) {
    const tailStart = match.index + match[0].length;
    const sentenceBoundary = sentenceBoundaryAfter(value, tailStart);
    if (
      sentenceBoundary !== undefined &&
      !isRecognizedSelectProse(value.slice(tailStart, sentenceBoundary), true)
    ) {
      return true;
    }
  }
  return false;
}

function rawPotentialSqlStart(value: string): number | undefined {
  SQL_SELECT_START.lastIndex = 0;
  for (const match of value.matchAll(SQL_SELECT_START)) {
    const tailStart = match.index + match[0].length;
    const sentenceBoundary = sentenceBoundaryAfter(value, tailStart);
    if (
      sentenceBoundary === undefined ||
      !isRecognizedSelectProse(value.slice(tailStart, sentenceBoundary), true)
    )
      return match.index;
  }
  return undefined;
}

function rawContainsUnsafeUnresolvedSelect(value: string): boolean {
  SQL_SELECT_START.lastIndex = 0;
  for (const match of value.matchAll(SQL_SELECT_START)) {
    const start = match.index + match[0].length;
    if (
      sentenceBoundaryAfter(value, start) === undefined &&
      !isRecognizedSelectProse(value.slice(start), false)
    ) {
      return true;
    }
  }
  return false;
}

function potentialSqlStart(value: string): number | undefined {
  const rawStart = rawPotentialSqlStart(value);
  if (rawStart !== undefined) return rawStart;
  const candidate = value
    .normalize("NFKC")
    .replace(/[\u200b-\u200f\u2060\ufeff]/g, "");
  return candidate !== value && rawPotentialSqlStart(candidate) !== undefined
    ? 0
    : undefined;
}

function containsUnsafeUnresolvedSelect(value: string): boolean {
  if (rawContainsUnsafeUnresolvedSelect(value)) return true;
  const candidate = value
    .normalize("NFKC")
    .replace(/[\u200b-\u200f\u2060\ufeff]/g, "");
  return candidate !== value && rawContainsUnsafeUnresolvedSelect(candidate);
}

function containsUnsafeCustomerTextDuringStream(
  value: string,
  activeOrganizationId: string,
): boolean {
  const normalized = value
    .normalize("NFKC")
    .replace(/[\u200b-\u200f\u2060\ufeff]/g, "");
  if (
    UNSAFE_CUSTOMER_TEXT.test(normalized) ||
    UNSAFE_UPPERCASE_SQL_TEXT.test(normalized) ||
    rawContainsUnsafeCompletedSelect(normalized)
  )
    return true;
  const active = activeOrganizationId.toLowerCase().replace(/[_-]+/g, " ");
  for (const customer of KNOWN_CUSTOMERS) {
    const customerForm = customer.toLowerCase().replace(/[_-]+/g, " ");
    if (customerForm === active) continue;
    const flexible = customerForm.replace(/ /g, "[\\s_-]+");
    if (
      new RegExp(`(?:^|[^a-z0-9])${flexible}(?=$|[^a-z0-9])`, "i").test(
        normalized,
      )
    ) {
      return true;
    }
  }
  return false;
}

export function containsUnsafeCustomerText(
  value: string,
  activeOrganizationId: string,
): boolean {
  return (
    containsUnsafeCustomerTextDuringStream(value, activeOrganizationId) ||
    containsUnsafeUnresolvedSelect(value)
  );
}

function parseEventRecord(record: string): NucleusTurnEvent | null {
  let eventName: string | undefined;
  let eventId: string | undefined;
  const dataLines: string[] = [];

  for (const line of record.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") eventName = value;
    if (field === "id") eventId = value;
    if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;

  let candidate: unknown;
  try {
    candidate = JSON.parse(dataLines.join("\n"));
  } catch {
    throw new NucleusSseError("Nucleus returned an invalid stream event");
  }

  if (
    !isRecord(candidate) ||
    !isStructurallyValidEvent(candidate) ||
    (eventName !== undefined && eventName !== candidate.type) ||
    (eventId !== undefined && eventId !== candidate.eventId)
  ) {
    throw new NucleusSseError("Nucleus returned an invalid stream event");
  }

  return candidate as NucleusTurnEvent;
}

export async function streamNucleusTurn({
  url,
  token,
  request,
  signal,
  onEvent,
  fetchImpl = fetch,
}: {
  url: string;
  token: string;
  request: EmbeddedTurnRequest;
  signal: AbortSignal;
  onEvent: (event: NucleusTurnEvent) => void;
  fetchImpl?: typeof fetch;
}): Promise<void> {
  if (!isAllowedNucleusStreamUrl(url)) {
    throw new NucleusSseError("Nucleus destination is not allowed");
  }

  const response = await fetchImpl(url, {
    method: "POST",
    redirect: "error",
    headers: {
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    throw new NucleusSseError(
      `Nucleus is unavailable (HTTP ${response.status})`,
      response.status,
    );
  }
  if (!response.headers.get("content-type")?.includes("text/event-stream")) {
    throw new NucleusSseError("Nucleus returned an unexpected response");
  }
  if (!response.body) {
    throw new NucleusSseError("Nucleus returned an empty response stream");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let turnId: string | undefined;
  let responseMessageId: string | undefined;
  let lastSequence: number | undefined;
  let declaredOutcome: NucleusTurnOutcome | undefined;
  let pendingTerminal: NucleusTurnEvent | undefined;
  let heldText = "";
  let heldTextEvent:
    | Extract<NucleusTurnEvent, { type: "text.delta" }>
    | undefined;
  const eventIds = new Set<string>();
  const expectedConversationId = request.conversationId ?? request.requestId;

  const validateStreamEvent = (event: NucleusTurnEvent) => {
    if (
      pendingTerminal ||
      event.conversationId !== expectedConversationId ||
      eventIds.has(event.eventId)
    ) {
      throw new NucleusSseError("Nucleus returned an invalid stream event");
    }

    const customerText =
      event.type === "text.delta"
        ? event.delta
        : event.type === "evidence.available"
          ? [
              event.label,
              event.evidence.title,
              event.evidence.summary ?? "",
              event.evidence.document?.title ?? "",
              event.evidence.document?.section ?? "",
              event.evidence.document?.excerpt ?? "",
              event.evidence.document?.freshness ?? "",
            ].join("\n")
          : event.type === "work.updated"
            ? event.label
            : event.type === "turn.failed"
              ? event.message
              : "";
    if (
      customerText &&
      (event.type === "text.delta"
        ? containsUnsafeCustomerTextDuringStream(
            customerText,
            request.organizationId,
          )
        : containsUnsafeCustomerText(customerText, request.organizationId))
    ) {
      throw new NucleusSseError("Nucleus returned an unsafe stream event");
    }

    if (lastSequence === undefined) {
      if (event.type !== "turn.started" || event.sequence !== 0) {
        throw new NucleusSseError("Nucleus returned an invalid stream event");
      }
      turnId = event.turnId;
      responseMessageId = event.messageId;
    } else if (
      event.type === "turn.started" ||
      event.turnId !== turnId ||
      event.sequence <= lastSequence
    ) {
      throw new NucleusSseError("Nucleus returned an invalid stream event");
    }

    if (
      (event.type === "text.delta" ||
        event.type === "turn.completed" ||
        event.type === "turn.cancelled") &&
      event.messageId !== responseMessageId
    ) {
      throw new NucleusSseError("Nucleus returned an invalid stream event");
    }

    if (event.type === "result.available" && event.outcome !== undefined) {
      if (declaredOutcome !== undefined) {
        throw new NucleusSseError("Nucleus returned an invalid stream event");
      }
      declaredOutcome = event.outcome;
    }
    if (TERMINAL_EVENT_TYPES.has(event.type) && declaredOutcome !== undefined) {
      const expectedFinishReason =
        nucleusTurnOutcomeFinishReason(declaredOutcome);
      if (
        event.type !== "turn.completed" ||
        event.finishReason !== expectedFinishReason
      ) {
        throw new NucleusSseError("Nucleus returned an invalid stream event");
      }
    }

    eventIds.add(event.eventId);
    lastSequence = event.sequence;
    if (TERMINAL_EVENT_TYPES.has(event.type)) pendingTerminal = event;
  };

  const emitHeldText = (flush: boolean, terminal = false) => {
    if (!heldTextEvent || heldText.length === 0) return;
    if (
      heldText.length > MAX_HELD_TEXT_CHARS ||
      containsUnsafeCustomerTextDuringStream(
        heldText,
        request.organizationId,
      ) ||
      (terminal && containsUnsafeCustomerText(heldText, request.organizationId))
    ) {
      throw new NucleusSseError("Nucleus returned an unsafe stream event");
    }
    let releaseLength = heldText.length;
    if (!flush) {
      const tokens = [...heldText.matchAll(/\s*\S+/g)];
      if (tokens.length <= HELD_TEXT_TOKENS) return;
      releaseLength = tokens[tokens.length - HELD_TEXT_TOKENS]?.index ?? 0;
    }
    if (!terminal) {
      const potentialSelectStart = potentialSqlStart(heldText);
      if (potentialSelectStart !== undefined) {
        releaseLength = Math.min(releaseLength, potentialSelectStart);
      }
    }
    if (releaseLength <= 0) return;
    const delta = heldText.slice(0, releaseLength);
    heldText = heldText.slice(releaseLength);
    onEvent({ ...heldTextEvent, delta });
  };

  const holdTextEvent = (
    event: Extract<NucleusTurnEvent, { type: "text.delta" }>,
  ) => {
    heldText += event.delta;
    heldTextEvent = event;
    emitHeldText(false);
  };

  const dispatchRecord = (record: string) => {
    if (record.length > MAX_SSE_EVENT_CHARS) {
      throw new NucleusSseError("Nucleus stream event is too large");
    }
    const event = parseEventRecord(record);
    if (!event) return;
    validateStreamEvent(event);
    if (event.type === "text.delta") {
      holdTextEvent(event);
    } else if (TERMINAL_EVENT_TYPES.has(event.type)) {
      emitHeldText(true, true);
    } else if (
      event.type === "work.updated" &&
      event.workId === "work-tracing_lineage" &&
      event.status === "started"
    ) {
      emitHeldText(true);
      onEvent(event);
    } else {
      onEvent(event);
    }
  };

  const dispatchCompleteRecords = () => {
    while (true) {
      const boundary = /\r?\n\r?\n/.exec(buffer);
      if (!boundary || boundary.index === undefined) return;
      const record = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary[0].length);
      dispatchRecord(record);
    }
  };

  let streamCompleted = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      dispatchCompleteRecords();
      if (buffer.length > MAX_SSE_BUFFER_CHARS) {
        throw new NucleusSseError("Nucleus stream buffer is too large");
      }
    }
    buffer += decoder.decode();
    dispatchCompleteRecords();
    if (buffer.trim()) dispatchRecord(buffer);
    if (pendingTerminal) onEvent(pendingTerminal);
    streamCompleted = true;
  } finally {
    if (!streamCompleted) {
      try {
        await reader.cancel();
      } catch {
        // Preserve the original validation or transport error.
      }
    }
    reader.releaseLock();
  }
}
